// Send a print to the Dhaba Print Service and follow it until it has printed.
//
// POST /print only queues the job (202). The printer may be off, out of paper,
// or the service may stop mid-send, so each job is followed (one shared poll of
// GET /jobs/recent, see tps.waitForJob) and the person is told when something
// needs their attention. Nothing here prints twice on its own: reprinting is
// always a button the person presses, and a job that may have printed asks
// "print again?" first.

import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
  DEMO_SKIP_JOB_ID,
  TPSConnectionError,
  TPSError,
  TPSPairingRequiredError,
  tps,
} from "@/lib/tps";
import { openTpsConnect } from "@/lib/tpsConnect";
import type { TPSJobStatus, TPSPrintRequest } from "@/types/printer";

/** How long to follow a job closely before saying it is waiting for the printer. */
export const PRINT_WATCH_MS = 60_000;
/** A job waiting for the printer is checked this often (inside the shared job poll). */
export const WAITING_POLL_MS = 10_000;
/**
 * How long to keep following a waiting job. The service drops a job that has not printed
 * within its job time limit (10 minutes by default, set per till; not exposed over HTTP),
 * so this is a generous cap: by then the job has printed or expired.
 */
export const WAITING_FOLLOW_MS = 60 * 60_000;

export type PrintOutcome =
  | { kind: "printed"; jobId: string }
  /** Demo outlet with printing off: nothing was sent. */
  | { kind: "skipped" }
  /** Still queued, printing or retrying when we stopped watching closely; it will print when the printer is back. */
  | { kind: "waiting"; jobId: string; status: TPSJobStatus; error: string | null; printerId: string | null }
  /** Did not print (with `copies` > 1 and status "failed", some copies may have). */
  | {
      kind: "failed";
      jobId: string;
      status: "failed" | "expired" | "cancelled";
      error: string | null;
      printerId: string | null;
      copies: number;
    }
  /** The printer stopped responding while printing: some or all of it may have printed. */
  | { kind: "uncertain"; jobId: string; error: string | null; printerId: string | null }
  /** Queued, but the service could not be asked how it went. */
  | { kind: "unconfirmed"; jobId: string }
  /** Never queued (or, for a timed-out request, maybe queued: see notify). */
  | { kind: "rejected"; error: unknown };

export interface PrintTicket {
  /** The service queued the job (or a demo outlet skipped it). */
  accepted: boolean;
  /** Settles when the job is final or we stop watching closely. Never rejects. */
  outcome: Promise<PrintOutcome>;
}

export interface SendToPrinterOptions {
  /** What is printing, for messages: "Bill", "KOT", "Test page". */
  label: string;
  /** Toast when it has printed (and when a demo outlet skipped it). Call sites that already say "sent to printer" leave this off. */
  notifySuccess?: boolean;
  /** How long to follow a job closely (PRINT_WATCH_MS). */
  watchMs?: number;
  /** Check at a fixed pace while following closely (default: the shared job poll's pace). */
  pollMs?: number;
  /** How often to check a job waiting for the printer (WAITING_POLL_MS). */
  waitingPollMs?: number;
  /** How long to keep following a job waiting for the printer (WAITING_FOLLOW_MS). */
  followMs?: number;
}

/** Same printer + same bytes while the first request is still being queued: reuse it (double clicks). */
const inFlight = new Map<string, Promise<PrintTicket>>();
/** Jobs being followed (closely, or waiting for the printer). */
const followUps = new Set<Promise<void>>();
/** Jobs the person cancelled from a toast: their "cancelled" state needs no second message. */
const cancelledHere = new Set<string>();

/** Settles when no job is being followed any more (tests). */
export async function printFollowUpsSettled(): Promise<void> {
  while (followUps.size > 0) await Promise.allSettled([...followUps]);
}

function flightKey(request: TPSPrintRequest): string {
  const target = request.printer_ids?.join(",") ?? request.printer_id ?? "default";
  return `${target}|${request.copies ?? 1}|${request.data}`;
}

export function sendToPrinter(request: TPSPrintRequest, options: SendToPrinterOptions): Promise<PrintTicket> {
  const key = flightKey(request);
  const existing = inFlight.get(key);
  if (existing) return existing;
  const ticket = queue(request, options).finally(() => inFlight.delete(key));
  inFlight.set(key, ticket);
  track(ticket.then((t) => t.outcome).then(() => undefined));
  return ticket;
}

function targetsOf(request: TPSPrintRequest): string[] {
  return request.printer_ids ?? [request.printer_id ?? "default"];
}

/** The same print, sent only to `printerIds` (all of the original targets when unknown). */
function narrowTo(request: TPSPrintRequest, printerIds: Array<string | null>): TPSPrintRequest {
  const known = printerIds.filter((id): id is string => !!id);
  const targets = targetsOf(request);
  if (known.length === 0 || (known.length === printerIds.length && targets.every((t) => known.includes(t)))) {
    return request;
  }
  const narrowed: TPSPrintRequest = { data: request.data };
  if (known.length === 1) narrowed.printer_id = known[0];
  else narrowed.printer_ids = known;
  if (request.copies !== undefined) narrowed.copies = request.copies;
  if (request.job_meta !== undefined) narrowed.job_meta = request.job_meta;
  return narrowed;
}

const SEVERITY: Record<PrintOutcome["kind"], number> = {
  printed: 0,
  skipped: 0,
  unconfirmed: 1,
  waiting: 2,
  failed: 3,
  uncertain: 4,
  rejected: 5,
};

async function queue(request: TPSPrintRequest, options: SendToPrinterOptions): Promise<PrintTicket> {
  let jobIds: string[];
  try {
    const res = await tps.print(request);
    jobIds = res.data.job_ids ?? (res.data.job_id ? [res.data.job_id] : []);
  } catch (error) {
    const outcome: PrintOutcome = { kind: "rejected", error };
    notify(outcome, request, options);
    return { accepted: false, outcome: Promise.resolve(outcome) };
  }

  if (jobIds.length === 0 || jobIds[0] === DEMO_SKIP_JOB_ID) {
    const skipped: PrintOutcome = { kind: "skipped" };
    notify(skipped, request, options);
    return { accepted: true, outcome: Promise.resolve(skipped) };
  }

  const targets = targetsOf(request);
  const outcome = Promise.all(jobIds.map((id, i) => follow(id, targets[i] ?? null, options))).then((outcomes) => {
    const worst = outcomes.reduce((a, b) => (SEVERITY[b.kind] > SEVERITY[a.kind] ? b : a));
    // "Print again" only on the printers that did not print.
    const redo = outcomes.filter(
      (o): o is Extract<PrintOutcome, { kind: "failed" | "uncertain" }> =>
        (o.kind === "failed" && o.status !== "cancelled") || o.kind === "uncertain",
    );
    const waiting = outcomes.filter((o): o is WaitingOutcome => o.kind === "waiting");
    // Worst is "waiting": its message stays only while a job under it still waits (WaitingToast).
    const waitingToast = worst.kind === "waiting" ? showWaiting(worst, waiting, options) : null;
    if (!waitingToast) notify(worst, redo.length > 0 ? narrowTo(request, redo.map((o) => o.printerId)) : request, options);
    for (const o of waiting) keepFollowing(o, request, options, waitingToast);
    return worst;
  });
  return { accepted: true, outcome };
}

function track(following: Promise<void>): void {
  const tracked = following.finally(() => followUps.delete(tracked));
  followUps.add(tracked);
}

type WaitingOutcome = Extract<PrintOutcome, { kind: "waiting" }>;

/**
 * The "… is waiting for the printer" message on screen and the jobs it is about
 * that still wait. It stays until closed only while one of them waits: once the
 * last one is settled it goes, and how that job ended (printed, did not print,
 * still hasn't printed) is said in its own message instead.
 */
interface WaitingToast {
  toast: ReturnType<typeof toast> | undefined;
  label: string;
  jobs: Set<string>;
  /** The job its Cancel print cancels (null: no Cancel). */
  cancelJobId: string | null;
}

function waitingMessage(label: string, cancelJobId: string | null) {
  return {
    title: `${label} is waiting for the printer`,
    description:
      "The printer is not responding. It will print by itself when the printer is back on — don't print it again. You'll get a message if it doesn't print." +
      (cancelJobId ? " Cancel it if you no longer need it." : ""),
    action: cancelJobId ? cancelAction(cancelJobId) : undefined,
  };
}

/** Says `worst` is waiting for the printer; the message goes once none of `waiting` waits any more. */
function showWaiting(worst: WaitingOutcome, waiting: WaitingOutcome[], options: SendToPrinterOptions): WaitingToast {
  const cancelJobId = cancellable(worst.status) ? worst.jobId : null;
  return {
    // Action needed while it waits: stays until closed (or until the job is settled).
    toast: toast({ ...waitingMessage(options.label, cancelJobId), important: true, duration: Infinity }),
    label: options.label,
    jobs: new Set(waiting.map((o) => o.jobId)),
    cancelJobId,
  };
}

/** `jobId` no longer waits: returns true when that took the waiting message off the screen. */
function settleWaiting(shown: WaitingToast | null, jobId: string): boolean {
  if (!shown || !shown.jobs.delete(jobId)) return false;
  if (shown.jobs.size === 0) {
    shown.toast?.dismiss();
    return true;
  }
  // Others still wait: the message stays, without a Cancel for a job that is settled.
  if (shown.cancelJobId === jobId) {
    shown.cancelJobId = null;
    const { description, action } = waitingMessage(shown.label, null);
    shown.toast?.update({ description, action });
  }
  return false;
}

/** Follow a job waiting for the printer until it prints, fails or expires, and say how it ended. */
function keepFollowing(
  waiting: WaitingOutcome,
  request: TPSPrintRequest,
  options: SendToPrinterOptions,
  shown: WaitingToast | null = null,
): void {
  const run = follow(waiting.jobId, waiting.printerId, options, {
    timeoutMs: options.followMs ?? WAITING_FOLLOW_MS,
    intervalMs: options.waitingPollMs ?? WAITING_POLL_MS,
  }).then((outcome) => {
    const { label } = options;
    const tookWaitingOff = settleWaiting(shown, waiting.jobId);
    if (outcome.kind === "printed") {
      toast({ title: `${label} printed`, description: "The printer is back and the print went through." });
    } else if (outcome.kind === "waiting") {
      toast({
        title: `${label} still hasn't printed`,
        description:
          "It has been waiting for the printer for a long time. Check the printer, and cancel the print if you no longer need it.",
        variant: "destructive",
        action: cancellable(outcome.status) ? cancelAction(outcome.jobId) : undefined,
        important: true,
        duration: Infinity,
      });
    } else if (outcome.kind === "failed" && outcome.status === "cancelled" && cancelledHere.has(outcome.jobId)) {
      // Cancelled from the waiting message: "Print cancelled" already said so.
    } else if (outcome.kind !== "unconfirmed" || tookWaitingOff) {
      // Lost track of it (unconfirmed): only said when it replaces "waiting", which promised a message.
      notify(outcome, narrowTo(request, [waiting.printerId]), options);
    }
  });
  track(run);
}

async function follow(
  jobId: string,
  target: string | null,
  options: SendToPrinterOptions,
  pace: { timeoutMs: number; intervalMs?: number } = { timeoutMs: options.watchMs ?? PRINT_WATCH_MS, intervalMs: options.pollMs },
): Promise<PrintOutcome> {
  try {
    const { job, final } = await tps.waitForJob(jobId, pace);
    if (!job) return { kind: "unconfirmed", jobId };
    const printerId = job.printer_id ?? target;
    if (!final) return { kind: "waiting", jobId, status: job.status, error: job.last_error, printerId };
    switch (job.status) {
      case "completed":
        return { kind: "printed", jobId };
      case "failed_unknown":
        return { kind: "uncertain", jobId, error: job.last_error, printerId };
      case "failed":
      case "expired":
      case "cancelled":
        return { kind: "failed", jobId, status: job.status, error: job.last_error, printerId, copies: job.copies ?? 1 };
      default:
        return { kind: "unconfirmed", jobId };
    }
  } catch {
    return { kind: "unconfirmed", jobId };
  }
}

/** The service only cancels a job that has not started printing. */
function cancellable(status: TPSJobStatus): boolean {
  return status === "queued" || status === "retrying";
}

/** A toast button that works once (a second click must not print a second copy). */
function onceAction(label: string, run: () => void) {
  let used = false;
  return (
    <ToastAction
      altText={label}
      onClick={() => {
        if (used) return;
        used = true;
        run();
      }}
    >
      {label}
    </ToastAction>
  );
}

function printAgain(request: TPSPrintRequest, options: SendToPrinterOptions) {
  return onceAction("Print again", () => {
    void sendToPrinter(request, options);
  });
}

/** "Print again" for a print that may already be on paper: asks once more before sending. */
function confirmPrintAgain(request: TPSPrintRequest, options: SendToPrinterOptions) {
  return onceAction("Print again", () => {
    toast({
      title: `Print ${options.label} again?`,
      description: "Only if it did not print. Check the printer first, or you may get it twice.",
      action: onceAction("Yes, print again", () => {
        void sendToPrinter(request, options);
      }),
      important: true,
      // Action needed: stays until answered or closed (no hover pause on a touch screen).
      duration: Infinity,
    });
  });
}

function cancelAction(jobId: string) {
  return onceAction("Cancel print", () => {
    cancelledHere.add(jobId);
    tps
      .cancelJob(jobId)
      .then(() => toast({ title: "Print cancelled" }))
      .catch((error: unknown) =>
        toast({
          title: "Couldn't cancel the print",
          description: error instanceof Error ? error.message : "Please try again.",
          variant: "destructive",
        }),
      );
  });
}

function failureReason(outcome: Extract<PrintOutcome, { kind: "failed" }>): string {
  if (outcome.status === "expired") {
    return "The printer did not come back in time, so the print service dropped the print. Nothing was printed.";
  }
  if (outcome.status === "cancelled") return "The print was cancelled.";
  const reason = outcome.error ? `The printer reported: ${outcome.error}` : "The printer could not print it.";
  return outcome.copies > 1
    ? `${reason} Some of the ${outcome.copies} copies may already have printed — check the printer before printing again.`
    : reason;
}

export function notify(outcome: PrintOutcome, request: TPSPrintRequest, options: SendToPrinterOptions): void {
  const { label } = options;
  switch (outcome.kind) {
    case "printed":
      if (options.notifySuccess) toast({ title: `${label} printed` });
      return;
    case "skipped":
      if (options.notifySuccess) {
        toast({
          title: "Printing is off for this demo outlet",
          description: "Nothing was sent to the printer. Turn on printing for this demo outlet in Settings › Print to print.",
        });
      }
      return;
    case "waiting":
      // sendToPrinter shows this itself (showWaiting), to take it off once the job is settled.
      toast({
        ...waitingMessage(label, cancellable(outcome.status) ? outcome.jobId : null),
        important: true,
        duration: Infinity,
      });
      return;
    case "failed":
      toast({
        title: `${label} did not print`,
        description: failureReason(outcome),
        variant: "destructive",
        action:
          outcome.status === "cancelled"
            ? undefined
            : outcome.copies > 1 && outcome.status === "failed"
              ? confirmPrintAgain(request, options)
              : printAgain(request, options),
        important: true,
        duration: outcome.status === "cancelled" ? 10_000 : Infinity,
      });
      return;
    case "uncertain":
      toast({
        title: `${label} may have printed`,
        description:
          "The printer stopped responding while it was printing, so part or all of it may have printed. Check the printer before printing again." +
          (outcome.error ? ` (${outcome.error})` : ""),
        variant: "destructive",
        action: confirmPrintAgain(request, options),
        important: true,
        duration: Infinity,
      });
      return;
    case "unconfirmed":
      toast({
        title: `${label} sent to the printer`,
        description: "Couldn't confirm that it printed. Check the printer.",
      });
      return;
    case "rejected": {
      const { error } = outcome;
      if (error instanceof TPSPairingRequiredError) {
        toast({
          title: `${label} not printed`,
          description: "Printing is not connected in this browser yet.",
          variant: "destructive",
          action: onceAction("Connect", openTpsConnect),
          important: true,
          duration: Infinity,
        });
        return;
      }
      if (error instanceof TPSConnectionError && error.reason === "timeout") {
        // The request may have reached the service before the answer was lost.
        toast({
          title: `${label} may have been sent`,
          description:
            "The print service did not answer in time. The print may have been sent — check the printer before printing again.",
          variant: "destructive",
          action: confirmPrintAgain(request, options),
          important: true,
          duration: Infinity,
        });
        return;
      }
      if (error instanceof TPSConnectionError) {
        toast({
          title: `${label} not printed`,
          description: "Can't reach the Dhaba Print Service on this computer.",
          variant: "destructive",
          action: onceAction("Help", openTpsConnect),
          important: true,
          duration: Infinity,
        });
        return;
      }
      if (error instanceof TPSError) {
        const retryable = error.code === "RATE_LIMITED" || error.code === "SHUTTING_DOWN" || error.code === "INTERNAL";
        toast({
          title: `${label} not printed`,
          description: error.message,
          variant: "destructive",
          action: retryable ? printAgain(request, options) : undefined,
          important: true,
          duration: Infinity,
        });
        return;
      }
      toast({
        title: `${label} not printed`,
        description: error instanceof Error ? error.message : "Could not send to printer.",
        variant: "destructive",
        important: true,
      });
    }
  }
}
